import React, { useEffect, useRef, useState } from "react"
import { withTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { showToast } from "utils/general"
import { addVideoMessage } from "helpers/backend_helper"
import {
  Alert,
  Button,
  Col,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Form,
  UncontrolledTooltip,
} from "reactstrap"
import { Switch, Select, Form as AntdForm, Input as AntdInput } from "antd"
import Box from "@mui/material/Box"
import Slider from "@mui/material/Slider"
import * as Yup from "yup"
import { useFormik } from "formik"
const { TextArea } = AntdInput

const VideoMessageModal = props => {
  const [saveDisable, setSaveDisable] = useState(false)
  const [newResponseOption, setNewResponseOption] = useState(1)
  const [newExpirationTime, setNewExpirationTime] = useState(15)
  const [newAlarmExpiration, setNewAlarmExpiration] = useState(false)
  const [audioAlert, setAudioAlert] = useState(true)
  const [errorMessage, setErrorMessage] = useState(null)

  const abortRef = useRef(null)
  useEffect(() => {
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  const handleResponseOptions = e => {
    setNewResponseOption(e)
  }

  // Setting alarm
  const settingExAlarm = () => {
    setNewAlarmExpiration(!newAlarmExpiration)
  }

  // Setting audio alarm
  const settingAudioAlarm = () => {
    setAudioAlert(!audioAlert)
  }

  // Slider expiration time
  const marks = [
    { value: 0, label: "0" },
    { value: 15, label: "15" },
    { value: 30, label: "30" },
    { value: 45, label: "45" },
    { value: 60, label: "60" },
  ]

  // format slider
  const handleSlider = (event, newValue) => {
    if (typeof newValue === "number") {
      setNewExpirationTime(newValue)
    }
  }

  // Formatting minutes
  const formatMinutes = value => {
    let Hours = Math.floor(value / 60)
    let minutes = value % 60

    let resultH = ""
    let resultM = ""

    if (Hours > 0) {
      resultH = Hours + "h"
    }
    if (Hours == 0) {
      resultH = ""
    }
    if (minutes > 0) {
      resultM = minutes + "min"
    }
    if (minutes == 0) {
      resultM = ""
    }
    if (minutes == 0 && Hours == 0) {
      resultM = minutes + "min"
    }

    return resultH + " " + resultM
  }

  // New video message
  const validationVideoMessage = useFormik({
    enableReinitialize: true,
    initialValues: {
      newMessageVideo: "",
    },
    validationSchema: Yup.object({
      newMessageVideo: Yup.string()
        .required(props.t("Please enter your message"))
        .trim()
        .min(5, props.t("Message is too short"))
        .max(50, props.t("Message is too long")),
    }),
    onSubmit: values => {
      if (props.data?.key) {
        setErrorMessage(null)
        setSaveDisable(true)
        addVideoMessage(
          abortRef.current,
          props.data.key,
          values.newMessageVideo,
          newResponseOption,
          newExpirationTime,
          newAlarmExpiration,
          audioAlert
        )
          .then(result => {
            //console.log(result)
            if (result.statusCode != null && result.statusCode == 200) {
              showToast(props.t("Message sent"), "success")

              if (props.closeModal) {
                props.closeModal()
              }

              // Set to formik
              validationVideoMessage.resetForm()
              setNewResponseOption(1)
              setNewExpirationTime(15)
              setNewAlarmExpiration(false)
              setAudioAlert(true)
            } else {
              setErrorMessage("Error")
            }
          })
          .catch(err => {
            console.log(err)
            setErrorMessage("Error")
          })
          .finally(() => {
            setSaveDisable(false)
          })
      }
    },
  })

  return (
    <>
      <Modal
        isOpen={props.isOpen}
        toggle={() => {
          if (props.closeModal) {
            props.closeModal()
          }
        }}
        centered
      >
        <ModalHeader>
          {props.t("Send a message to")} {props.data?.name}
        </ModalHeader>

        <Form
          onSubmit={e => {
            e.preventDefault()
            return false
          }}
        >
          <ModalBody>
            <div className="mt-1">
              {errorMessage ? (
                <Alert color="danger">{props.t(errorMessage)}</Alert>
              ) : null}
              <Label>{props.t("Message")}</Label>
              <AntdForm.Item
                validateStatus={validationVideoMessage.errors.newMessageVideo ? "error" : ""}
                hasFeedback
                help={validationVideoMessage.errors.newMessageVideo}
              >
                <TextArea
                  allowClear
                  maxLength={50}
                  showCount
                  placeholder={props.t("Message")}
                  value={validationVideoMessage.values.newMessageVideo}
                  onChange={(v) => { validationVideoMessage.setFieldValue("newMessageVideo", v.target.value) }}
                />
              </AntdForm.Item>
            </div>
            <div className="mt-4">
              <Row>
                <Col xs={12}>
                  <FormGroup>
                    <Label>{props.t("Response options")}</Label>
                    <Select
                      value={newResponseOption}
                      placeholder={props.t("Choose")}
                      onChange={(v) => { handleResponseOptions(v) }}
                      options={[
                        { label: props.t("Ok"), value: 1 },
                        { label: props.t("Yes / No"), value: 2 },
                      ]}
                      style={{ width: 100 + "%" }}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </div>
            <div className="mt-2">
              <Label>
                {props.t("Message expiration time")}{" "}
                {formatMinutes(newExpirationTime)}
              </Label>
              <div className="p-2">
                <Box sx={{ width: "100%" }}>
                  <Slider
                    size="small"
                    valueLabelDisplay="auto"
                    aria-labelledby="non-linear-slider"
                    defaultValue={30}
                    getAriaValueText={formatMinutes}
                    valueLabelFormat={formatMinutes}
                    step={1}
                    onChange={handleSlider}
                    marks={marks}
                    min={0}
                    value={newExpirationTime}
                    max={60}
                  />
                </Box>
              </div>
            </div>
            <Row>
              <Col xs={6}>
                <div className="mt-4 mb-0">
                  <FormGroup>
                    <Label>{props.t("Expiration alarm")}</Label>
                    <Switch
                      className="ms-2"
                      name="arrange_time"
                      checked={newAlarmExpiration}
                      onChange={settingExAlarm}
                      checkedChildren={
                        <i className="mdi mdi-bell-check-outline align-middle font-size-14" />
                      }
                      unCheckedChildren={
                        <i className="mdi mdi-bell-off-outline align-middle font-size-14" />
                      }
                    ></Switch>
                  </FormGroup>
                </div>
              </Col>
              <Col xs={6}>
                <div className="mt-4 mb-0">
                  <FormGroup>
                    <Label>{props.t("Message tone")}</Label>
                    <Switch
                      className="ms-2"
                      name="arrange_time"
                      checked={audioAlert}
                      onChange={settingAudioAlarm}
                      checkedChildren={
                        <i className="mdi mdi-bell-check-outline align-middle font-size-14" />
                      }
                      unCheckedChildren={
                        <i className="mdi mdi-bell-off-outline align-middle font-size-14" />
                      }
                    ></Switch>
                    <div id="info-circle" className=" d-md-inline-block">
                      <Button
                        className="pz-2 py-0"
                        size="sm"
                        style={{ cursor: "default" }}
                        color="tranparent"
                        onClick={() => { }}
                      >
                        <i className="bx bx-info-circle font-size-10 " />
                      </Button>
                    </div>
                    <UncontrolledTooltip placement="right" target="info-circle">
                      {props.t("Video device message tone on/off")}
                    </UncontrolledTooltip>
                  </FormGroup>
                </div>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter className="">
            <div className="float-end">
              <Button
                color="primary"
                outline
                onClick={() => {
                  if (props.closeModal) {
                    props.closeModal()
                  }
                }}
                data-dismiss="modal"
              >
                {props.t("Cancel")}
              </Button>
              <Button
                color="primary"
                disabled={saveDisable || !props.data?.key}
                className="ms-2"
                onClick={() => {
                  validationVideoMessage.submitForm()
                }}
              >
                {props.t("Save entry")}
              </Button>
            </div>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  )
}

VideoMessageModal.propTypes = {
  t: PropTypes.any,
  data: PropTypes.any,
  isOpen: PropTypes.bool,
  closeModal: PropTypes.func,
}

export default withTranslation()(VideoMessageModal)
